/**
 * PHARMAKON — Trésorerie · the pure model of « Règlements » (TWP-26): G1 « Soldes fournisseurs », G2
 * « Régler un fournisseur » (the settlement presentation of the one movement form, E-8
 * `VentilationGrid`, E-9 `VentilationQuestion`), G3 « Journal des règlements », G5 the settlement
 * after confirmation, G6 the facture side and the fournisseur's T2 drawer.
 *
 * design/ux/03_reglements_journaux_tiers_dettes.md §3-§8, §14.3; 02_ARCHITECTURE §6.2-§6.7; 03_UX_DESIGN
 * §2.4, §5.3. The server is the authority on every figure (`POST /reglements/ventilation` answers the
 * six §16 figures, the source and fournisseur « avant → après », the refusals, the rights and the
 * preview hash): this module only decides WHAT the form holds and sends — the ventilation mode, the
 * checked factures, the typed parts, whether the amount follows the selection, the request bodies,
 * which gesture a role sees, why F9 is disabled, which question Entrée asks. It never adds, subtracts
 * or rounds an amount: money stays the server's decimal strings, only compared (zero / sign / order)
 * and passed through (03_UX_DESIGN P-5, AC-G2-12). No DOM, no copy (the messages config words it).
 */
import { addDecimal, compareDecimal, parseDecimalInput, toDecimalString } from "@/lib/number-format.js";
import {
  JOURNAL_REGLEMENTS_FILTRE_STATUT,
  RECHERCHE_FOURNISSEUR_MIN,
  SOLDES_FILTRE_ECHEANCE,
  SOLDES_FILTRE_SOLDE,
  SOLDES_FILTRE_SOLDE_DEFAUT,
  SOLDES_TRI,
  SOLDES_TRI_DEFAUT,
} from "@/config/tresorerie.config.js";
import { ajusterMode, jourOfficine, modesAdmis, referenceRequise } from "@/lib/tresorerie/operations.js";
import { STATUT_REGLEMENT } from "@/config/reglement-achat.config.js";
import { OBJETS_PANNEAU, PARAM_ONGLET_PANNEAU, PARAM_PANNEAU, valeurPanneau } from "@/lib/tresorerie/panneaux.js";

/** `ModeVentilation` (Prisma) — the three presentations of the grid (UX 03 §4.4). */
export const MODES_VENTILATION = Object.freeze({ AUTO: "AUTO", SELECTION: "SELECTION", MANUEL: "MANUEL" });
export const MODES_VENTILATION_ORDRE = Object.freeze([MODES_VENTILATION.AUTO, MODES_VENTILATION.SELECTION, MODES_VENTILATION.MANUEL]);
/** The letters of the Ventilation control and of the E-9 question (bare keys, never in a text field). */
export const LETTRES_VENTILATION = Object.freeze({ a: MODES_VENTILATION.AUTO, s: MODES_VENTILATION.SELECTION, m: MODES_VENTILATION.MANUEL });
/** E-9 — « … ou sur toutes les factures ouvertes (n) » (key T): the automatic ventilation without the checked rows. */
export const CHOIX_TOUTES = "TOUTES";

/** `StatutOperationTresorerie` (Prisma) of a settlement. */
export const STATUTS_REGLEMENT = Object.freeze({ BROUILLON: "BROUILLON", CONFIRME: "CONFIRME", ANNULE: "ANNULE" });
/** `StatutReglementAchat` (Prisma) — the facture's status (the « reglement_facture » status registry). */
export const STATUTS_FACTURE = Object.freeze({ NON_REGLEE: "NON_REGLEE", PARTIELLEMENT_REGLEE: "PARTIELLEMENT_REGLEE", REGLEE: "REGLEE" });
/** The A-02 sense of a solde (`fournisseur-solde.service.lireSolde`). */
export const SENS_SOLDE = Object.freeze({ A_PAYER: "A_PAYER", CREDIT: "CREDIT", A_JOUR: "A_JOUR" });

/** Server codes the form reacts to (RC-64: the sentence is the server's, the code only routes it). */
export const CODES_REGLEMENT = Object.freeze({
  PREVIEW_PERIME: "PREVIEW_PERIME",
  DEPASSE_RESTE_DU: "REGLEMENT_DEPASSE_RESTE_DU",
  SOLDE_INSUFFISANT: "EMPLACEMENT_FONDS_SOLDE_INSUFFISANT",
  FORBIDDEN: "FORBIDDEN",
});

/** `ModeReglement` keys the defaults name; the list and the « référence requise » flag are served by the référentiel. */
const M_CHEQUE = "CHEQUE";

// ─── Rights (served by GET /referentiel — never a hand-kept mirror, UX 03 §2) ──────────────────────

/**
 * The settlement gestures of the caller (`permissions.reglement_achat`, resolved by the server with
 * the TD-45 setting — RC-49): `payer` (F9 « Confirmer le règlement »), `preparer` (Ctrl+S — a
 * Brouillon; « Préparer un règlement » when `payer` is false: TD-187), `annuler`, `lire`.
 */
export function droitsReglements(referentiel) {
  const r = new Set(referentiel?.permissions?.reglement_achat ?? []);
  const lire = r.has("read");
  return Object.freeze({ lire, payer: r.has("pay"), preparer: r.has("prepare"), annuler: r.has("cancel"), regler: r.has("pay") || r.has("prepare") });
}

// ─── The « Règlements » leaf (tabs in the path, 03_UX_DESIGN §2.2) ─────────────────────────────────

/** The tabs of the leaf (« Charges » — TWP-27, UX 03 §13: the non-supplier obligations, `charge:read`). */
export const ONGLETS_REGLEMENTS = Object.freeze(["soldes", "journal", "charges"]);
export const ROUTE_REGLEMENTS = "/tresorerie/reglements";
export const routeOnglet = (onglet) => `${ROUTE_REGLEMENTS}/${onglet}`;
/** TA-25 — the leaf lands on « Soldes » for whoever may settle, else on the journal (read-only M, TD-187). */
export function ongletParDefaut(droits) {
  return droits?.regler ? ONGLETS_REGLEMENTS[0] : ONGLETS_REGLEMENTS[1];
}
export const ongletValide = (o, droits) => (ONGLETS_REGLEMENTS.includes(o) ? o : ongletParDefaut(droits));

/** The palette verb « Régler un fournisseur » and the F3 entry: `?regler=1` (optionally `&fournisseur=<id>`). */
export const PARAM_REGLER = "regler";
export const PARAM_FOURNISSEUR = "fournisseur";

// ─── TWP-28 — the legacy URLs of the WP-53 consumer contract (UX 03 §6.2; TD-51, TD-131) ───────────

const UUID_FOURNISSEUR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The legacy parameter of `/achats/reglements` that named the tab (the tabs are in the path now). */
export const PARAM_ONGLET_HERITE = "tab";
/** The parameter `GET /finance/mon-releve` took and « Mon relevé » links carried. */
export const PARAM_FOURNISSEUR_HERITE = "fournisseur_id";

const lireRecherche = (search) => new URLSearchParams(String(search ?? "").replace(/^\?/, ""));
const ecrireRecherche = (p) => {
  const s = p.toString();
  return s ? `?${s}` : "";
};

/** The fournisseur's T2 drawer (`?panneau=tiers:fournisseur:<id>`), on `onglet` when given; an id that is not one is dropped. */
function ouvrirFournisseurDans(p, fournisseurId, onglet = null) {
  if (!fournisseurId || !UUID_FOURNISSEUR.test(fournisseurId) || p.has(PARAM_PANNEAU)) return;
  p.set(PARAM_PANNEAU, valeurPanneau(OBJETS_PANNEAU.TIERS, cleTiersFournisseur({ fournisseur_id: fournisseurId })));
  if (onglet) p.set(PARAM_ONGLET_PANNEAU, onglet);
}

/**
 * `/achats/reglements` — the refonte WP-53 URL (D-22) — lands on the one page `/tresorerie/reglements`
 * (TO-02, TD-51): `?tab=<soldes|journal|charges>` becomes the tab of the path (the page lands on its
 * role's tab when none is named, TA-25), `?fournisseur=<id>` opens that fournisseur's drawer (T2) — or,
 * beside `?regler=1`, stays the settlement form's fournisseur; every other parameter is kept. A tab or
 * an id the page does not know is dropped, never forwarded.
 * @param {string} search the legacy URL's query string (with or without « ? »)
 * @returns {string} the target path and query string
 */
export function redirectionAchatsReglements(search) {
  const p = lireRecherche(search);
  const tab = p.get(PARAM_ONGLET_HERITE);
  p.delete(PARAM_ONGLET_HERITE);
  if (!p.has(PARAM_REGLER) && p.has(PARAM_FOURNISSEUR)) {
    const fournisseur = p.get(PARAM_FOURNISSEUR);
    p.delete(PARAM_FOURNISSEUR);
    ouvrirFournisseurDans(p, fournisseur);
  }
  const chemin = ONGLETS_REGLEMENTS.includes(tab) ? routeOnglet(tab) : ROUTE_REGLEMENTS;
  return `${chemin}${ecrireRecherche(p)}`;
}

/**
 * `/mon-releve` (TD-131) → « Règlements › Soldes fournisseurs » — the relevé is per fournisseur, in its
 * drawer (RC-115): `?fournisseur_id=<id>` (or `?fournisseur=`) opens that drawer on « Relevé ». Every
 * other parameter is kept. The target's guard applies (`reglement_achat:read`, RC-48).
 * @param {string} search
 * @returns {string}
 */
export function redirectionMonReleve(search) {
  const p = lireRecherche(search);
  const fournisseur = p.get(PARAM_FOURNISSEUR_HERITE) ?? p.get(PARAM_FOURNISSEUR);
  p.delete(PARAM_FOURNISSEUR_HERITE);
  p.delete(PARAM_FOURNISSEUR);
  ouvrirFournisseurDans(p, fournisseur, "releve");
  return `${routeOnglet("soldes")}${ecrireRecherche(p)}`;
}

/** The legacy URLs whose query string is rewritten on the way (App.tsx `LegacyRedirect`, ROUTE_REDIRECTS). */
export const REDIRECTIONS_REGLEMENTS = Object.freeze({
  "/achats/reglements": redirectionAchatsReglements,
  "/mon-releve": redirectionMonReleve,
});

// ─── G1 — « Soldes fournisseurs » filters (URL-synced, UX 03 §3.4) ──────────────────────────────────

const unDe = (valeurs, v, defaut = null) => (Object.values(valeurs).includes(v) ? v : defaut);

/**
 * The `GET /reglements/soldes` params of the G1 state (the FilterBar's URL values + the sort and the
 * page): an unknown value is never sent (the server's default applies), a search shorter than the
 * server's minimum is not sent (the field says why), the defaults are omitted.
 * @param {{ q?: string, solde?: string, echeance?: string, connecte?: string|boolean, tri?: string, page?: number|string }} v
 * @param {{ limite?: number, rechercheMin?: number }} [opts]
 */
export function paramsSoldes(v = {}, { limite, rechercheMin = RECHERCHE_FOURNISSEUR_MIN } = {}) {
  const p = {};
  const q = String(v.q ?? "").trim();
  if (q.length >= rechercheMin) p.q = q;
  const solde = unDe(SOLDES_FILTRE_SOLDE, v.solde);
  if (solde && solde !== SOLDES_FILTRE_SOLDE_DEFAUT) p.solde = solde;
  const echeance = unDe(SOLDES_FILTRE_ECHEANCE, v.echeance);
  if (echeance && echeance !== SOLDES_FILTRE_ECHEANCE.TOUTES) p.echeance = echeance;
  if (v.connecte === true || v.connecte === "true" || v.connecte === "1") p.connecte = "true";
  const tri = unDe(SOLDES_TRI, v.tri);
  if (tri && tri !== SOLDES_TRI_DEFAUT) p.tri = tri;
  const page = Number.parseInt(String(v.page ?? "1"), 10);
  if (Number.isFinite(page) && page > 1) p.page = page;
  if (limite) p.limite = limite;
  return p;
}

/** Is the search typed but still too short to be sent (« Saisissez au moins 2 caractères. »)? */
export const rechercheTropCourte = (q, rechercheMin = RECHERCHE_FOURNISSEUR_MIN) => {
  const t = String(q ?? "").trim();
  return t.length > 0 && t.length < rechercheMin;
};

/** The sort the grid shows for a server `tri` (each key is sorted one way by the server). */
export const triAffiche = (tri) => {
  const t = unDe(SOLDES_TRI, tri, SOLDES_TRI_DEFAUT);
  return { by: Object.keys(TRI_PAR_COLONNE_INTERNE).find((c) => TRI_PAR_COLONNE_INTERNE[c] === t) ?? "solde", order: ORDRE_TRI_INTERNE[t] };
};

/** A G1 sort header → the server's `tri` (the four the server whitelists; another column is not sortable). */
const TRI_PAR_COLONNE_INTERNE = Object.freeze({
  fournisseur: SOLDES_TRI.FOURNISSEUR,
  reglements: SOLDES_TRI.REGLEMENTS,
  solde: SOLDES_TRI.SOLDE,
  echeance: SOLDES_TRI.ECHEANCE,
});
export const TRI_PAR_COLONNE = TRI_PAR_COLONNE_INTERNE;
/** The server sorts each key one way (solde / règlements decreasing, échéance increasing, name A→Z). */
const ORDRE_TRI_INTERNE = Object.freeze({
  [SOLDES_TRI.SOLDE]: "desc",
  [SOLDES_TRI.REGLEMENTS]: "desc",
  [SOLDES_TRI.ECHEANCE]: "asc",
  [SOLDES_TRI.FOURNISSEUR]: "asc",
});
export const ORDRE_TRI = ORDRE_TRI_INTERNE;

/** Is this row a debt the role may settle or prepare (« Régler »), else only its « Relevé » (UX 03 §3.2)? */
export function actionLigneSolde(ligne, droits) {
  if (droits?.regler && ligne?.solde?.sens === SENS_SOLDE.A_PAYER) return droits.payer ? "regler" : "preparer";
  return "releve";
}

/** « Imputer le crédit… » (P; solde < 0 and ≥ 1 open facture — TD-43, UX 03 §3.5). */
export const imputationCreditPossible = (ligne, droitImputer) =>
  Boolean(droitImputer) && ligne?.solde?.sens === SENS_SOLDE.CREDIT && (ligne?.nb_non_reglees ?? 0) > 0;

// ─── G2 — the settlement form (UX 03 §4) ───────────────────────────────────────────────────────────

/**
 * The empty settlement form of an entry point (UX 03 §6.1):
 *   - G1 row / F3 / Ctrl+K: the fournisseur (or none) — focus « Montant » (or « Fournisseur »);
 *   - T2 « Régler la sélection », a facture (S3, facture drawer, E1): the checked factures in mode S,
 *     the amount FOLLOWING their Σ reste (`montantLie`, the server computes it — « = Σ sélection »);
 *   - a Brouillon to confirm / continue: its values (`brouillon`).
 * `montant` is null (follows) or the user's decimal string. `parts` (mode M) maps a facture id to the
 * typed decimal string; `proposees` keeps the server's proposal per cell (Ctrl+Retour arrière).
 */
export function etatInitialReglement({ fournisseur = null, factures = [], montant = null, aujourdhui, source = null, avoirs = null } = {}) {
  const selection = [...new Set(factures ?? [])];
  return {
    // TWP-66 (TO-17) — the avoir rows imputed first: null = every one offered (« Factures ouvertes »), else the ids chosen
    avoirs: Array.isArray(avoirs) ? [...new Set(avoirs)] : null,
    fournisseur: fournisseur ? { id: fournisseur.id, nom: fournisseur.nom ?? fournisseur.raison_sociale ?? null } : null,
    montant: montant != null ? toDecimalString(montant) : null,
    // A selection arriving with its factures: the amount follows their Σ reste until typed (TD-188).
    montantLie: montant == null && selection.length > 0,
    // No mode until the user answers the question (or arrives with a selection: Sélection).
    mode: selection.length > 0 ? MODES_VENTILATION.SELECTION : null,
    selection,
    parts: {},
    proposees: {},
    source,
    modeReglement: null,
    modeReglementChoisi: false,
    reference: "",
    date_operation: aujourdhui,
    date_confirmation: aujourdhui,
    date_echeance: null,
    note: "",
  };
}

/** A fournisseur change resets the ventilation (one fournisseur per règlement, TD-55) and keeps the payment fields. */
export function changerFournisseur(etat, fournisseur) {
  if ((etat.fournisseur?.id ?? null) === (fournisseur?.id ?? null)) return etat;
  return { ...etat, fournisseur: fournisseur ? { id: fournisseur.id, nom: fournisseur.nom ?? fournisseur.raison_sociale ?? null } : null, mode: null, selection: [], parts: {}, proposees: {}, montantLie: false };
}

/** The user typed the amount: it stops following the selection / the parts (F8 links it again). */
/**
 * RC-CP-15 (TWP-56) — is the amount handed back by the field the one already shown? A blur without a
 * keystroke (or the field leaving the dialog) hands back the displayed value: nothing was typed, so a
 * following amount must keep following and the dialog must not turn « dirty ». Empty = null.
 */
export function memeMontant(saisi, affiche) {
  const vide = (v) => v == null || v === "";
  if (vide(saisi) || vide(affiche)) return vide(saisi) && vide(affiche);
  return compareDecimal(saisi, affiche) === 0;
}

export function saisirMontant(etat, montant) {
  return { ...etat, montant: montant == null || montant === "" ? null : toDecimalString(montant), montantLie: false };
}

/** F8 — « Montant = total de la sélection » (S) / « Montant = total ventilé » (M): the amount follows again. */
export function lierMontant(etat) {
  if (etat.mode !== MODES_VENTILATION.SELECTION && etat.mode !== MODES_VENTILATION.MANUEL) return etat;
  return { ...etat, montant: null, montantLie: true };
}

/** Has the mode M a typed part (switching away asks « Abandonner la saisie par facture ? »)? */
export const partsSaisies = (etat) => Object.values(etat.parts ?? {}).some((v) => v != null && v !== "" && compareDecimal(v, "0") > 0);

/**
 * Switch the ventilation mode (the Ventilation control, Alt+V then A / S / M). Entering M starts from
 * the current allocation (each cell = what the last preview imputed — « la valeur proposée »), so the
 * user adjusts rather than types everything; leaving M drops the parts (the caller asked first).
 * @param {object} etat
 * @param {string} mode
 * @param {object|null} apercu the last settled preview (its `factures[].impute`)
 */
export function changerMode(etat, mode, apercu = null) {
  if (!MODES_VENTILATION_ORDRE.includes(mode) || etat.mode === mode) return etat;
  const n = { ...etat, mode };
  if (mode === MODES_VENTILATION.MANUEL) {
    const proposees = {};
    for (const f of apercu?.factures ?? []) if (f.impute != null && compareDecimal(f.impute, "0") > 0) proposees[f.id] = f.impute;
    n.parts = { ...proposees };
    n.proposees = proposees;
    // The amount typed stays; an amount that followed the selection now follows the parts.
    if (etat.montantLie || etat.montant == null) n.montantLie = true;
  } else {
    n.parts = {};
    n.proposees = {};
    if (mode === MODES_VENTILATION.AUTO) {
      // An amount that followed the selection / the parts keeps the server's figure, now typed.
      if (etat.montantLie && apercu?.montant != null) n.montant = apercu.montant;
      n.montantLie = false;
    }
    if (mode === MODES_VENTILATION.SELECTION && etat.montant == null) n.montantLie = true;
  }
  return n;
}

/**
 * E-9 — the answer to « Voulez-vous ventiler ce montant ? »: A (automatic — on the checked factures
 * when some are checked, Scenario 3's path), T (automatic on every open facture), S (choose the
 * factures), M (type per facture).
 */
export function repondreQuestion(etat, choix, apercu = null) {
  if (choix === CHOIX_TOUTES) return { ...changerMode({ ...etat, mode: null }, MODES_VENTILATION.AUTO, apercu), selection: [] };
  if (choix === MODES_VENTILATION.AUTO && etat.selection.length > 0) {
    // « Ventiler automatiquement sur les factures cochées (n) » = SELECTION with the typed amount.
    return { ...etat, mode: MODES_VENTILATION.SELECTION, montantLie: false, parts: {}, proposees: {} };
  }
  return changerMode(etat, choix, apercu);
}

/** Espace on a row (mode S): check / uncheck — a non-réglable row never enters the selection. */
export function basculerFacture(etat, id, reglables) {
  if (reglables && !reglables.has(id)) return etat;
  const selection = etat.selection.includes(id) ? etat.selection.filter((x) => x !== id) : [...etat.selection, id];
  return { ...etat, selection, montantLie: etat.montantLie || etat.montant == null };
}

/** Ctrl+A / Maj+↑↓ — the selection the grid reports, restricted to the réglable rows. */
export function selectionner(etat, ids, reglables) {
  const selection = [...new Set(ids)].filter((id) => !reglables || reglables.has(id));
  return { ...etat, selection, montantLie: etat.montantLie || etat.montant == null };
}

/**
 * The text of a « Par facture » cell (a digit or « , » started it; the comma is the French separator):
 * `{ valeur }` a decimal string or null (emptied), `{ erreur }` the sentence that keeps the editor open.
 */
export function lirePart(texte) {
  const r = parseDecimalInput(texte, { decimals: 2, allowNegative: false });
  return r.error ? { valeur: null, erreur: r.error } : { valeur: r.value == null ? null : toDecimalString(r.value), erreur: null };
}

/** Mode M — one cell committed: « » or 0 empties it (Suppr); a text that is not an amount changes nothing. */
export function saisirPart(etat, factureId, valeur) {
  const lu = lirePart(valeur ?? "");
  if (lu.erreur) return etat;
  const v = lu.valeur;
  const parts = { ...etat.parts };
  if (v == null || compareDecimal(v, "0") <= 0) delete parts[factureId];
  else parts[factureId] = v;
  return { ...etat, parts };
}

/** Ctrl+Retour arrière — « Rétablir la valeur proposée » of a cell (mode M). */
export function retablirPart(etat, factureId) {
  const parts = { ...etat.parts };
  if (etat.proposees?.[factureId] != null) parts[factureId] = etat.proposees[factureId];
  else delete parts[factureId];
  return { ...etat, parts };
}

const texteOuNull = (v) => (v == null || String(v).trim() === "" ? null : String(v).trim());

/**
 * The body of `POST /reglements/ventilation` (the preview, every field typed so far — the server
 * names what is missing). `montant` null = it follows the selection / the parts (the server answers
 * the amount, `montant_suit`). Without a mode yet, the automatic order is previewed (the grid shows
 * the factures, nothing is ventilated until the user answers).
 */
export function corpsApercuReglement(etat, { operationId = null, avoirs = [] } = {}) {
  const corps = operationId ? { operation_id: operationId } : { fournisseur_id: etat.fournisseur?.id };
  const mode = etat.mode ?? MODES_VENTILATION.AUTO;
  corps.mode_ventilation = mode;
  // No mode yet: the factures are listed, nothing ventilated (« Ventilé — ») until the user answers;
  // AUTO ventilates the typed amount; S / M send null while the amount follows the selection / parts.
  corps.montant = etat.mode == null ? null : mode === MODES_VENTILATION.AUTO || !etat.montantLie ? etat.montant : null;
  if (mode === MODES_VENTILATION.SELECTION) corps.factures = [...etat.selection];
  if (mode === MODES_VENTILATION.MANUEL) {
    corps.lignes = Object.entries(etat.parts ?? {})
      .filter(([, v]) => v != null && compareDecimal(v, "0") > 0)
      .map(([facture_achat_id, montant]) => ({ facture_achat_id, montant: toDecimalString(montant) }));
  }
  if (etat.source) corps.source_emplacement_id = etat.source;
  if (etat.modeReglement) corps.mode = etat.modeReglement;
  corps.reference = texteOuNull(etat.reference);
  // TWP-66 — the avoir rows (never on an existing settlement, never « Par facture »: `avoirsEffectifs`)
  if (!operationId && avoirs?.length) corps.avoirs = [...avoirs];
  return corps;
}

/**
 * The body of `POST /reglements` (Brouillon or Confirmé): the preview's fields + the dates, the note,
 * the preview hash (TA-17 — the writer re-checks it under the facture locks) and the form's idempotency
 * key (a retry is a replay, never a second payment — TA-16). A Brouillon carries no date de valeur.
 */
export function corpsCreationReglement(etat, { statut, previewHash = null, idempotencyKey = null, avoirs = [] }) {
  // TWP-66 — a draft never imputes avoirs (the form refuses Ctrl+S while some are checked)
  const { operation_id: _op, ...apercu } = corpsApercuReglement(etat, { avoirs: statut === STATUTS_REGLEMENT.CONFIRME ? avoirs : [] });
  const corps = { ...apercu, statut_cible: statut };
  if (etat.date_operation) corps.date_operation = etat.date_operation;
  if (statut === STATUTS_REGLEMENT.CONFIRME && etat.date_confirmation) corps.date_confirmation = etat.date_confirmation;
  corps.date_echeance = etat.date_echeance || null;
  corps.note = texteOuNull(etat.note);
  if (previewHash) corps.preview_hash = previewHash;
  if (idempotencyKey) corps.idempotency_key = idempotencyKey;
  return corps;
}

/** TD-44 — a cheque dated after today stays a Brouillon until it is cashed (primary « Enregistrer le chèque différé »). */
export function chequeDiffere(etat, aujourdhui) {
  return etat.modeReglement === M_CHEQUE && Boolean(etat.date_echeance) && Boolean(aujourdhui) && etat.date_echeance > aujourdhui;
}

/** Is the amount a positive decimal (gates the question; the server stays authoritative)? */
export function montantPositif(v) {
  const d = toDecimalString(v);
  return d != null && compareDecimal(d, "0") > 0;
}

/** Is a decimal string zero (« Reste à ventiler 0,00 ✓ »)? */
export const estZero = (v) => v != null && compareDecimal(v, "0") === 0;

/**
 * TD-42 — the over-payment: the typed amount exceeds Σ reste of every réglable open facture (the
 * question is replaced by the server's refusal under « Montant »). `totalDu` = the server's
 * `totaux.reste_reglable` of the factures-ouvertes read.
 */
export function depassement(montant, totalDu) {
  if (!montantPositif(montant) || totalDu == null) return false;
  return compareDecimal(montant, totalDu) > 0;
}

/**
 * E-9 — does Entrée in « Montant » ask « Voulez-vous ventiler ce montant ? » (TA-36): a fournisseur, an
 * amount > 0, a SETTLED preview (never while one is in flight) and « Reste à ventiler » ≠ 0 — or no
 * mode chosen yet. Over-payment shows the refusal instead (TD-42). Else Entrée moves on.
 * @returns {"question"|"depassement"|"suivant"|"attendre"}
 */
export function gesteEntreeMontant(etat, { apercu, apercuAJour, totalDu }) {
  if (!etat.fournisseur || !montantPositif(etat.montant)) return "suivant";
  if (depassement(etat.montant, totalDu)) return "depassement";
  if (!apercuAJour) return "attendre";
  if (etat.mode == null) return "question";
  if (apercu?.reste_a_ventiler != null && !estZero(apercu.reste_a_ventiler)) return "question";
  return "suivant";
}

/** The first refusal of the preview on a field (`champ`), or among the general ones. */
export const refusDe = (apercu, champ) => (apercu?.refus ?? []).find((r) => (r.champ ?? null) === champ) ?? null;

/**
 * Why F9 « Confirmer le règlement » is disabled (written next to it, 01F §3.1), or null. The order
 * reads like the form: the fournisseur, the preview, the remainder, the source, then the server's
 * first refusal verbatim.
 * @returns {{ cle: string, message?: string, montant?: string } | null}
 */
export function raisonConfirmation(etat, { apercu, apercuAJour, droits, aujourdhui }) {
  if (!droits?.payer) return { cle: "droit" };
  if (!etat.fournisseur) return { cle: "fournisseur" };
  if (chequeDiffere(etat, aujourdhui)) return { cle: "chequeDiffere" };
  if (!apercuAJour || !apercu) return { cle: "attente" };
  if (etat.mode == null) return { cle: "ventiler", montant: apercu.reste_a_ventiler ?? apercu.montant ?? null };
  if (apercu.reste_a_ventiler != null && compareDecimal(apercu.reste_a_ventiler, "0") > 0) return { cle: "reste", montant: apercu.reste_a_ventiler };
  if (!apercu.source) return { cle: "source" };
  const r = apercu.refus?.[0];
  if (r) return { cle: "refus", message: r.message };
  if (apercu.confirmable === false) return { cle: "attente" };
  return null;
}

/** Why Ctrl+S (Brouillon) is refused now, or null: a Brouillon may be un-ventilated (TD-119), not refused. */
export function raisonBrouillon(etat, { apercu, apercuAJour, droits }) {
  if (!droits?.preparer && !droits?.payer) return { cle: "droit" };
  if (!etat.fournisseur) return { cle: "fournisseur" };
  if (!apercuAJour || !apercu) return { cle: "attente" };
  const bloquant = (apercu.refus ?? []).find((r) => !r.brouillon);
  if (bloquant) return { cle: "refus", message: bloquant.message };
  return null;
}

/** The sentence key of the footer's line 1 when something remains to ventilate (UX 03 §4.7). */
export function phraseReste(etat, apercu) {
  if (!apercu || apercu.reste_a_ventiler == null) return null;
  if (estZero(apercu.reste_a_ventiler)) return "ok";
  if (etat.mode == null) return "ventilez";
  if (etat.mode === MODES_VENTILATION.SELECTION) return "cochez";
  if (etat.mode === MODES_VENTILATION.MANUEL) return "saisissez";
  return "reduisez";
}

/**
 * 409 `PREVIEW_PERIME` (UX 03 §4.4 / §4.9): the rows whose « Reste avant » changed between the preview
 * the user confirmed and the fresh one — `{ [id]: ancienReste }` (the « Modifiée depuis » chip).
 */
export function lignesModifiees(ancien, frais) {
  const avant = new Map((ancien?.factures ?? []).map((f) => [f.id, f.avant?.reste ?? null]));
  const out = {};
  for (const f of frais?.factures ?? []) {
    const a = avant.get(f.id);
    if (a === undefined) continue;
    if (a != null && f.avant?.reste != null && compareDecimal(a, f.avant.reste) !== 0) out[f.id] = a;
  }
  return out;
}

/**
 * The grid rows of a preview (E-8): the réglable factures in the server's order, each with what the
 * presentation shows in its first column (rank / checkbox / blank), whether the amount reaches it,
 * and the non-réglable ones listed apart (« Non réglables ici »).
 */
export function lignesVentilation(apercu, etat) {
  const sel = new Set(etat.selection ?? []);
  return (apercu?.factures ?? []).map((f) => ({
    ...f,
    cochee: sel.has(f.id),
    part: etat.mode === MODES_VENTILATION.MANUEL ? etat.parts?.[f.id] ?? null : null,
    proposee: etat.proposees?.[f.id] ?? null,
    nonAtteinte: etat.mode === MODES_VENTILATION.AUTO && !f.atteinte,
    nonSelectionnee: etat.mode === MODES_VENTILATION.SELECTION && !sel.has(f.id),
  }));
}

/** The réglable ids of a preview (the selection never holds another row). */
export const idsReglables = (apercu) => new Set((apercu?.factures ?? []).map((f) => f.id));

/** The field a server refusal belongs to (form keys). */
export function champDuRefus(champ) {
  if (!champ) return null;
  const c = String(champ).replace(/^(body|query|params)\./, "");
  if (c === "source_emplacement_id") return "source";
  if (c === "fournisseur_id") return "fournisseur";
  if (c === "mode") return "modeReglement";
  return c;
}

/** Split refusals (preview `refus` or a write's error body) per form field + the general ones. */
export function refusParChamp(source) {
  const parChamp = {};
  const generales = [];
  const poser = (champ, message) => {
    if (!message) return;
    const c = champDuRefus(champ);
    if (c && !parChamp[c]) parChamp[c] = message;
    else if (!c && !generales.includes(message)) generales.push(message);
  };
  if (!source) return { parChamp, generales };
  if (Array.isArray(source.refus)) for (const r of source.refus) poser(r.facture_achat_id ? null : r.champ, r.message);
  else if (Array.isArray(source.errors) && source.errors.length) for (const e of source.errors) poser(e.field, e.message);
  else poser(source.details?.champ ?? null, source.message);
  return { parChamp, generales };
}

/** The refusals of a preview that name one facture row (`facture_achat_id`): shown on that row. */
export function refusParFacture(apercu) {
  const out = {};
  for (const r of apercu?.refus ?? []) if (r.facture_achat_id && !out[r.facture_achat_id]) out[r.facture_achat_id] = r.message;
  return out;
}

// ─── G3 — « Journal des règlements » (UX 03 §5) ────────────────────────────────────────────────────

/**
 * The G3 query from the URL: period (`du` / `au`, officine days), search, fournisseur, modes,
 * « Payé depuis », statuts (default Confirmé + Brouillon — TD-191, server default when absent).
 */
export function filtresJournal(params) {
  const jour = (v) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
  const uuid = (v) => (v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v : null);
  const statuts = (params.get("statut") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => Object.values(JOURNAL_REGLEMENTS_FILTRE_STATUT).includes(s));
  return {
    du: jour(params.get("du")),
    au: jour(params.get("au")),
    q: (params.get("q") ?? "").trim(),
    fournisseur_id: uuid(params.get("fournisseur_id")),
    tiers_id: uuid(params.get("tiers_id") ?? params.get("tiers")),
    mode: (params.get("mode") ?? "").split(",").map((m) => m.trim()).filter(Boolean),
    compte_id: uuid(params.get("compte_id")),
    statut: statuts,
  };
}

/** The `GET /reglements` params of a G3 state (empty filters omitted; the cursor appended by the caller). */
export function paramsJournal(f, limite) {
  const p = {};
  for (const k of ["du", "au", "q", "fournisseur_id", "tiers_id", "compte_id"]) if (f[k]) p[k] = f[k];
  if (f.mode?.length) p.mode = f.mode.join(",");
  if (f.statut?.length) p.statut = f.statut.join(",");
  if (limite) p.limite = limite;
  return p;
}

/** Line 1 of « Date / N° »: the value date of a confirmed settlement, « prévu » + its date for a Brouillon. */
export function dateJournal(op) {
  if (op.statut === STATUTS_REGLEMENT.BROUILLON) return { prevu: true, date: op.date_echeance ?? op.date_operation ?? op.created_at };
  return { prevu: false, date: op.date_confirmation ?? op.date_operation ?? op.created_at };
}

/** The gestures of a journal row from the server's verdicts (`actions` when served, else the role's rights). */
export function gestesLigneJournal(op, droits) {
  const brouillon = op.statut === STATUTS_REGLEMENT.BROUILLON;
  const annule = op.statut === STATUTS_REGLEMENT.ANNULE;
  return {
    ouvrir: true,
    confirmer: Boolean(brouillon && droits?.payer),
    // A confirmed settlement is modified with `reglement_achat:pay`, a draft with `prepare` (the drawer's verdict decides).
    modifier: Boolean(!annule && (brouillon ? droits?.preparer || droits?.payer : droits?.payer)),
    annuler: Boolean(!annule && droits?.annuler),
    imprimer: !brouillon && !annule && !op.montant_masque,
  };
}

// ─── G5 — a settlement after its creation: modify (Alt+O), confirm a draft, cancel (UX 03 §7) ──────

/** The live lines of a settlement (a draft: its proposed allocation) as `[{ facture_achat_id, montant }]`. */
function lignesDuReglement(op) {
  if (op?.statut === STATUTS_REGLEMENT.BROUILLON) {
    const proposees = Array.isArray(op.allocation_proposee) ? op.allocation_proposee : [];
    return proposees.map((l) => ({ facture_achat_id: l.facture_achat_id, montant: toDecimalString(l.montant) }));
  }
  return (op?.allocations ?? [])
    .filter((l) => l.statut !== STATUTS_REGLEMENT.ANNULE)
    .map((l) => ({ facture_achat_id: l.facture?.id ?? l.facture_achat_id, montant: toDecimalString(l.montant) }));
}

/**
 * G2 in MODIFICATION mode (UX 03 §7.2): the form state of an existing settlement — Confirmé or
 * Brouillon — read from its drawer (GET /operations/:id). The fournisseur is locked (another one =
 * cancel and recreate), the amount typed (it never « follows » until the user asks with F8), the mode
 * and its inputs (the checked factures in Sélection, the parts in Par facture), the payment fields
 * and the dates in officine days. A confirmed settlement without a stored mode (one line, legacy) reads
 * as « Par facture » — the server's own reading (`MODE_VENTILATION_LIGNE_UNIQUE`).
 */
export function etatDepuisReglement(op, { fuseau = null } = {}) {
  const lignes = lignesDuReglement(op);
  const brouillon = op?.statut === STATUTS_REGLEMENT.BROUILLON;
  const mode = op?.mode_ventilation ?? (brouillon ? MODES_VENTILATION.AUTO : MODES_VENTILATION.MANUEL);
  const parts = {};
  if (mode === MODES_VENTILATION.MANUEL) for (const l of lignes) if (compareDecimal(l.montant, "0") > 0) parts[l.facture_achat_id] = l.montant;
  const fournisseur = op?.ventilation?.fournisseur?.id ?? op?.tiers?.fournisseur_id ?? null;
  return {
    fournisseur: fournisseur ? { id: fournisseur, nom: op?.ventilation?.fournisseur?.nom ?? op?.tiers?.nom ?? null } : null,
    montant: op?.montant != null ? toDecimalString(op.montant) : null,
    montantLie: false,
    mode,
    selection: mode === MODES_VENTILATION.SELECTION ? lignes.map((l) => l.facture_achat_id) : [],
    parts,
    proposees: { ...parts },
    source: op?.source?.id ?? null,
    modeReglement: op?.mode ?? null,
    modeReglementChoisi: true,
    reference: op?.reference ?? "",
    date_operation: jourOfficine(op?.date_operation, fuseau),
    date_confirmation: brouillon ? jourOfficine(op?.date_operation, fuseau) : jourOfficine(op?.date_confirmation, fuseau),
    date_echeance: jourOfficine(op?.date_echeance, fuseau),
    note: op?.note ?? "",
  };
}

const memesIds = (a, b) => {
  const x = [...new Set(a ?? [])].sort();
  const y = [...new Set(b ?? [])].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
};
function memesParts(a, b) {
  const cles = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
  for (const k of cles) {
    const u = a?.[k] ?? "0";
    const v = b?.[k] ?? "0";
    if (compareDecimal(u, v) !== 0) return false;
  }
  return true;
}
const memeTexteReglement = (a, b) => (texteOuNull(a) ?? null) === (texteOuNull(b) ?? null);

/**
 * What a modification changes (UX 03 §7.2), compared with the settlement as it was opened:
 *   `changements` the header fields (`PATCH /operations/:id` → `changements`, the CONFIG's editable
 *     fields only): the amount (the typed one, or the server's when it follows the selection / the
 *     parts), « Payer depuis », the mode, the reference, the dates (the value date of a confirmed one
 *     only), the note;
 *   `ventilation` true when the allocation's inputs moved (mode, checked factures, parts) — or the amount
 *     (the re-allocation is the grid the user saw, sent as such).
 * @param {object} etat the form · @param {object} base `etatDepuisReglement` of the opened settlement
 * @param {{ statut: string, montantServeur?: string|null }} opts
 */
export function changementsReglement(etat, base, { statut, montantServeur = null } = {}) {
  const c = {};
  const montant = etat.montantLie ? montantServeur : etat.montant;
  if (montant != null && base.montant != null && compareDecimal(montant, base.montant) !== 0) c.montant = toDecimalString(montant);
  if ((etat.source ?? null) !== (base.source ?? null) && etat.source) c.source_emplacement_id = etat.source;
  if ((etat.modeReglement ?? null) !== (base.modeReglement ?? null) && etat.modeReglement) c.mode = etat.modeReglement;
  if (!memeTexteReglement(etat.reference, base.reference)) c.reference = texteOuNull(etat.reference);
  if ((etat.date_operation ?? null) !== (base.date_operation ?? null) && etat.date_operation) c.date_operation = etat.date_operation;
  if (statut === STATUTS_REGLEMENT.CONFIRME && (etat.date_confirmation ?? null) !== (base.date_confirmation ?? null) && etat.date_confirmation) {
    c.date_confirmation = etat.date_confirmation;
  }
  if ((etat.date_echeance ?? null) !== (base.date_echeance ?? null)) c.date_echeance = etat.date_echeance || null;
  if (!memeTexteReglement(etat.note, base.note)) c.note = texteOuNull(etat.note);
  const ventilation =
    etat.mode !== base.mode ||
    (etat.mode === MODES_VENTILATION.SELECTION && !memesIds(etat.selection, base.selection)) ||
    (etat.mode === MODES_VENTILATION.MANUEL && !memesParts(etat.parts, base.parts)) ||
    "montant" in c;
  return { changements: c, ventilation };
}

/** The class of a modification for its footer sentence: a ventilation or a FINANCIER field moves money, else descriptive. */
export function classeModificationReglement({ changements, ventilation }, referentiel) {
  if (ventilation) return "FINANCIER";
  const classes = referentiel?.champs_operation?.modifiables ?? {};
  return Object.keys(changements ?? {}).some((k) => classes[k] === "FINANCIER") ? "FINANCIER" : "DESCRIPTIF";
}

/**
 * The body of `PATCH /operations/:id` for a settlement (If-Match = the version opened): the changed
 * header fields, the grid's allocation when it moved (+ the hash of the preview the user saw — TA-17;
 * a draft's proposal is replaced without one, it reserves nothing), the motif.
 */
export function corpsModificationReglement(etat, base, { statut, apercu = null, motif = "" } = {}) {
  const { changements, ventilation } = changementsReglement(etat, base, { statut, montantServeur: apercu?.montant ?? null });
  const corps = {};
  if (Object.keys(changements).length) corps.changements = changements;
  if (ventilation) {
    const v = { mode_ventilation: etat.mode };
    if (etat.mode === MODES_VENTILATION.SELECTION) v.factures = [...etat.selection];
    if (etat.mode === MODES_VENTILATION.MANUEL) {
      v.lignes = Object.entries(etat.parts ?? {})
        .filter(([, m]) => m != null && compareDecimal(m, "0") > 0)
        .map(([facture_achat_id, m]) => ({ facture_achat_id, montant: toDecimalString(m) }));
    }
    corps.ventilation = v;
    if (statut === STATUTS_REGLEMENT.CONFIRME && apercu?.preview_hash) corps.preview_hash = apercu.preview_hash;
  }
  if (texteOuNull(motif)) corps.motif = texteOuNull(motif);
  return corps;
}

/** Does this modification change anything (F9 / Ctrl+S disabled with « Aucune modification » otherwise)? */
export const modificationVide = (corps) => !corps.changements && !corps.ventilation;

/**
 * The factures a re-allocation changes, for its footer and its ConfirmDialog (UX 03 §7.2): each facture
 * whose imputed amount moves — « avant » its reste today (the drawer's « reste aujourd'hui » for a facture
 * this settlement imputes, else the preview's « avant »), « après » the preview's reste after. Server
 * strings only, compared, never computed.
 * @param {object|null} apercu `POST /reglements/ventilation` with `operation_id`
 * @param {object|null} ventilation the drawer's `ventilation` (GET /operations/:id)
 */
export function facturesChangees(apercu, ventilation) {
  const aujourdhui = new Map((ventilation?.lignes ?? []).filter((l) => !l.remplacee).map((l) => [l.facture?.id, l.reste_aujourdhui]));
  return (apercu?.factures ?? [])
    .filter((f) => compareDecimal(f.impute ?? "0", f.impute_actuel ?? "0") !== 0)
    .map((f) => ({ id: f.id, libelle: f.libelle, avant: aujourdhui.get(f.id) ?? f.avant?.reste ?? null, apres: f.apres?.reste ?? null, statut: f.apres?.statut ?? null, impute: f.impute }));
}

/**
 * Why F9 « Enregistrer la modification » (a confirmed settlement) is disabled, or null: nothing changed,
 * the motif (≥ `min` characters), the settled preview, a remainder (a confirmed settlement imputes all
 * its amount), the source, the server's first refusal.
 */
export function raisonModification(corps, { apercu, apercuAJour, motif = "", min = 3, financier = true }) {
  if (modificationVide(corps)) return { cle: "aucune" };
  if ((texteOuNull(motif) ?? "").length < min) return { cle: "motif", min };
  if (!financier) return null;
  if (!apercuAJour || !apercu) return { cle: "attente" };
  if (apercu.reste_a_ventiler != null && compareDecimal(apercu.reste_a_ventiler, "0") > 0) return { cle: "reste", montant: apercu.reste_a_ventiler };
  if (!apercu.source) return { cle: "source" };
  const r = apercu.refus?.[0];
  if (r) return { cle: "refus", message: r.message };
  return null;
}

/**
 * A draft's confirmation from G3 / G5 (UX 03 §5.4): the fresh preview of the draft (`operation_id`, its
 * stored inputs) confirms it as is only when it still ventilates exactly what the draft proposed — every
 * part equal, nothing left, no refusal, a source. Otherwise the confirmation opens G2 on the draft with
 * that fresh preview instead (« a changed ventilation opens G2 »).
 * @returns {{ direct: boolean, raison: string|null }}
 */
export function verdictConfirmationBrouillon(op, apercu) {
  if (!apercu) return { direct: false, raison: "attente" };
  if ((apercu.refus ?? []).length) return { direct: false, raison: "refus" };
  if (!apercu.source) return { direct: false, raison: "source" };
  if (apercu.reste_a_ventiler != null && compareDecimal(apercu.reste_a_ventiler, "0") !== 0) return { direct: false, raison: "reste" };
  const proposees = new Map(lignesDuReglement(op).filter((l) => compareDecimal(l.montant, "0") > 0).map((l) => [l.facture_achat_id, l.montant]));
  const fraiches = new Map((apercu.lignes ?? []).filter((l) => compareDecimal(l.montant, "0") > 0).map((l) => [l.facture_achat_id, l.montant]));
  if (proposees.size !== fraiches.size) return { direct: false, raison: "ventilation" };
  for (const [id, m] of proposees) if (!fraiches.has(id) || compareDecimal(fraiches.get(id), m) !== 0) return { direct: false, raison: "ventilation" };
  return { direct: true, raison: null };
}

// ─── TD-43 — « Imputer le crédit » (UX 03 §4.11): the supplier credit applied explicitly ───────────

/**
 * The empty credit imputation of a fournisseur (G1 row menu, T2, the G2 band): the automatic
 * ventilation, the amount FOLLOWING — the server imputes as much of the credit as the open factures
 * take (`montant_suit`, never more than the credit) until the user types a smaller amount. No
 * source, no payment mode: no money moves (TD-43). The same grid state as G2 (`selection`, `parts`,
 * `proposees`), so the E-8 gestures (Espace, Ctrl+A, the cells, Ctrl+Retour arrière) apply unchanged.
 */
export function etatInitialCredit(fournisseur) {
  return {
    fournisseur: fournisseur ? { id: fournisseur.id, nom: fournisseur.nom ?? fournisseur.raison_sociale ?? null } : null,
    montant: null,
    montantLie: true,
    mode: MODES_VENTILATION.AUTO,
    selection: [],
    parts: {},
    proposees: {},
  };
}

/** Is the amount the server's (« = crédit disponible imputable », F8 links it again) or the user's? */
export const montantCreditLie = (etat) => etat.montantLie || etat.montant == null;

/** F8 — « Montant = crédit imputable »: the amount follows the server's default again. */
export const lierMontantCredit = (etat) => ({ ...etat, montant: null, montantLie: true });

/**
 * Switch the credit's ventilation mode (A / S / M): the grid inputs as in G2 (`changerMode`: M starts
 * from the preview's imputations), the amount untouched — a typed amount stays typed, a following one
 * keeps following in the new mode (the server's default for that mode).
 */
export function changerModeCredit(etat, mode, apercu = null) {
  if (!MODES_VENTILATION_ORDRE.includes(mode) || etat.mode === mode) return etat;
  const n = changerMode(etat, mode, apercu);
  return { ...n, montant: montantCreditLie(etat) ? null : etat.montant, montantLie: montantCreditLie(etat) };
}

/**
 * The body of `POST /reglements/credit/ventilation` (strict: no source, no mode, no reference):
 * the fournisseur, the mode and its inputs, the typed amount or null (the server's default).
 */
export function corpsApercuCredit(etat) {
  const mode = etat.mode ?? MODES_VENTILATION.AUTO;
  const corps = { fournisseur_id: etat.fournisseur?.id, mode_ventilation: mode, montant: montantCreditLie(etat) ? null : toDecimalString(etat.montant) };
  if (mode === MODES_VENTILATION.SELECTION) corps.factures = [...etat.selection];
  if (mode === MODES_VENTILATION.MANUEL) {
    corps.lignes = Object.entries(etat.parts ?? {})
      .filter(([, v]) => v != null && compareDecimal(v, "0") > 0)
      .map(([facture_achat_id, montant]) => ({ facture_achat_id, montant: toDecimalString(montant) }));
  }
  return corps;
}

/** The body of `POST /reglements/credit`: the preview's inputs + its hash (TA-17) + the optional motif. */
export function corpsImputationCredit(etat, { previewHash, motif = "" } = {}) {
  const corps = { ...corpsApercuCredit(etat), preview_hash: previewHash };
  const m = texteOuNull(motif);
  if (m) corps.motif = m;
  return corps;
}

/**
 * Why F9 « Imputer le crédit » is disabled, or null: the settled preview, the role (P only — the
 * server's verdict), the server's first refusal verbatim (the credit exceeded, nothing to impute, a
 * row refused), a remainder (S / M: what the factures cannot take), a preview that moves nothing.
 * @returns {{ cle: string, message?: string|null, montant?: string } | null}
 */
export function raisonImputationCredit(etat, { apercu, apercuAJour }) {
  if (!etat?.fournisseur) return { cle: "fournisseur" };
  if (!apercuAJour || !apercu) return { cle: "attente" };
  const droit = apercu.droits?.imputer;
  if (droit && droit.autorise === false) return { cle: "refus", message: droit.raison ?? null };
  const r = apercu.refus?.[0];
  if (r) return { cle: "refus", message: r.message };
  if (apercu.reste_a_ventiler != null && compareDecimal(apercu.reste_a_ventiler, "0") > 0) return { cle: "reste", montant: apercu.reste_a_ventiler };
  if (apercu.confirmable === false) return { cle: "rien" };
  return null;
}

/** The credit's sources the preview draws on (the factures covered beyond their base), the ones it uses first. */
export function sourcesCredit(apercu) {
  const sources = apercu?.sources ?? [];
  return [...sources.filter((s) => compareDecimal(s.impute ?? "0", "0") > 0), ...sources.filter((s) => compareDecimal(s.impute ?? "0", "0") <= 0)];
}

/**
 * TWP-56 (TO-11) — a confirmed avoir's credit no facture carries yet, announced by the server
 * (`credit_avoirs: { nb, total }` of the open factures / the T2 profile) and the caller's right to impute
 * it (`{ autorise }`, P + M — the server's verdict). The test reads the server's decimal string.
 */
export const creditAvoirsImputable = (credit, droit) => Boolean(droit?.autorise) && compareDecimal(credit?.total ?? "0", "0") > 0;

/**
 * Does the G2 band offer « Imputer le crédit… »? An open facture, and either a credit in the officine's
 * favour with the owner's right (TD-43) or an avoir credit the caller may impute (TWP-56).
 */
export const creditImputableDepuisBande = (ouvertes, droitImputer) =>
  (ouvertes?.totaux?.nb_reglables ?? 0) > 0 &&
  ((Boolean(droitImputer) && ouvertes?.solde?.sens === SENS_SOLDE.CREDIT) || creditAvoirsImputable(ouvertes?.credit_avoirs, ouvertes?.droits?.imputer_avoir));

/**
 * TWP-56 — the « Imputer le crédit » preview holds no settlement line paid beyond its facture but avoir
 * credits: the TD-43 form part (amount, ventilation, grid) gives way to one sentence and the avoirs
 * are the dialog's only actions. Before the first preview: false (the form shows « Calcul… »).
 */
export const sansCreditReglements = (apercu) =>
  Boolean(apercu) && !(compareDecimal(apercu.credit?.imputable ?? "0", "0") > 0) && (apercu.credit?.nb_avoirs ?? 0) > 0;

/** The same question from the T2 header (`GET /tiers/:clé/profil` figures). */
export const creditImputableDepuisProfil = (figures, droitImputer) =>
  (figures?.factures_ouvertes?.nombre ?? 0) > 0 &&
  ((Boolean(droitImputer) && figures?.solde?.sens === SENS_SOLDE.CREDIT) || creditAvoirsImputable(figures?.credit_avoirs, figures?.credit_avoirs?.imputer));

// ─── D5 « Règlement » — the compact G2 section of a purchase validation (UX 03 §6.3, W12) ────────────
//
// One facture, no grid: the amount is D5's own (« Réglée » / « Réglée partiellement »), this section
// names « Payer depuis », the mode and the référence the settlement owner checks before the validation
// moves anything (`POST /achats/:id/validate` `reglement.{source_emplacement_id, mode, reference}`,
// TWP-25). The account starts from the one the server would take (`source_reglement_defaut_id`,
// TD-190 — shown, never silent), the mode from that account's type (TD-10, `ajusterMode`).

/** May the caller declare a payment at validation (the effective `reglement_achat:pay`, TD-45)? */
export const paiementValidationPossible = (referentiel) => droitsReglements(referentiel).payer;

/** A declared statut (« Réglée » / « Réglée partiellement ») asks for the payment's account and mode. */
export const paiementDeclare = (statut) => statut === STATUT_REGLEMENT.REGLEE || statut === STATUT_REGLEMENT.PARTIELLEMENT_REGLEE;

const compteParId = (comptes, id) => (id ? (comptes ?? []).find((c) => c.id === id) ?? null : null);

/**
 * The opening state of the section: the server's default account (when it is among the accounts the
 * caller reads), its type's mode, no reference.
 * @returns {{ source: string|null, mode: string|null, modeChoisi: boolean, reference: string }}
 */
export function paiementValidationInitial(referentiel, comptes = []) {
  const defaut = referentiel?.source_reglement_defaut_id ?? null;
  const source = compteParId(comptes, defaut) ? defaut : null;
  const r = ajusterMode({ mode: null, modeChoisi: false }, { source: compteParId(comptes, source), destination: null, referentiel });
  return { source, mode: r.valeurs.mode ?? null, modeChoisi: false, reference: "" };
}

/** « Payer depuis » changed: the mode follows the account (an unchosen mode silently, a chosen one replaced and SAID). */
export function changerSourcePaiement(valeur, source, { comptes, referentiel }) {
  const r = ajusterMode({ mode: valeur.mode, modeChoisi: valeur.modeChoisi }, { source: compteParId(comptes, source), destination: null, referentiel });
  return { valeur: { ...valeur, source, mode: r.valeurs.mode ?? valeur.mode, modeChoisi: r.valeurs.modeChoisi ?? valeur.modeChoisi }, annonce: r.annonce };
}

/** The modes the chosen account admits (the référentiel's matrix, `controle_mode_compte_strict`). */
export const modesPaiementValidation = (valeur, { comptes, referentiel }) => modesAdmis(compteParId(comptes, valeur?.source), null, referentiel);

/** Does the chosen mode need a référence (chèque, virement — RC-41)? */
export const referencePaiementRequise = (valeur, referentiel) => referenceRequise(valeur?.mode, referentiel);

/**
 * Why D5 cannot send this payment yet, or null: a mode the account does not admit, a missing
 * référence. The account itself may stay empty only when the server names it (no default served).
 * @returns {{ champ: "mode"|"reference", cle: string }|null}
 */
export function raisonPaiementValidation(valeur, { comptes, referentiel }) {
  if (!valeur) return null;
  if (valeur.source && valeur.mode && !modesPaiementValidation(valeur, { comptes, referentiel }).includes(valeur.mode)) return { champ: "mode", cle: "modeRefuse" };
  if (referencePaiementRequise(valeur, referentiel) && !String(valeur.reference ?? "").trim()) return { champ: "reference", cle: "referenceRequise" };
  return null;
}

/**
 * The `reglement` body of the validation: D5's statut / amount, plus the payment's account, mode and
 * référence when a payment is declared (never on « Non réglée »).
 */
export function corpsReglementValidation(envoye, valeur) {
  if (!envoye || !paiementDeclare(envoye.statut) || !valeur) return envoye ?? null;
  const reference = String(valeur.reference ?? "").trim();
  return {
    ...envoye,
    ...(valeur.source ? { source_emplacement_id: valeur.source } : {}),
    ...(valeur.mode ? { mode: valeur.mode } : {}),
    ...(reference ? { reference } : {}),
  };
}

/**
 * What the validation's answer says about the payment (TD-190 « never silent », RC-37): the written
 * settlement (amount, account, mode, its number), or the warning when it could not be written.
 * @returns {{ ton: "success"|"warning", montant?: string, source?: string|null, mode?: string|null, numero?: string|null, message?: string }|null}
 */
export function issuePaiementValidation(reponse) {
  const b = reponse?.reglement ?? null;
  const avert = b?.avertissement ?? reponse?._reglement_avertissement ?? null;
  if (b?.ecrit) return { ton: "success", montant: b.montant, source: b.source?.libelle ?? null, mode: b.mode_libelle ?? b.mode ?? null, numero: b.numero ?? null };
  if (avert) return { ton: "warning", message: typeof avert === "string" ? avert : avert.message ?? null };
  return null;
}

// ─── G5 / G6 — after the confirmation, the facture side ────────────────────────────────────────────

/** « Régler » on a facture (G6): the server's gate (`droits.regler`, `reglable`) — never re-derived here. */
export function gesteReglerFacture(bloc) {
  const g = bloc?.droits?.regler;
  if (!g) return { etat: "masque", raison: null };
  if (g.autorise) return { etat: "actif", mode: g.mode, raison: null };
  // A role that may neither pay nor prepare never sees the button (P-11); a state reason is written.
  if (bloc?.droits?.confirmer?.autorise === false && bloc?.droits?.brouillon?.autorise === false) return { etat: "masque", raison: null };
  return { etat: "desactive", raison: g.raison ?? bloc?.reglable?.message ?? null };
}

/**
 * The settlement form's context for « Régler » on ONE facture (G6, the S3 entry — UX 03 §6.1): its
 * fournisseur (locked) and the facture checked (mode S, amount = its reste); null while the block does
 * not name its fournisseur.
 * @param {object|null} bloc `GET /achats/:id/reglements`
 */
export function contexteReglerFacture(bloc) {
  const f = bloc?.fournisseur;
  if (!f?.id || !bloc?.facture_achat_id) return null;
  return { fournisseur: { id: f.id, nom: f.nom ?? null }, factures: [bloc.facture_achat_id] };
}

/** The live imputations of a facture block first, the cancelled ones after (struck). */
export function imputationsFacture(bloc) {
  const lignes = bloc?.reglements ?? [];
  const vivantes = lignes.filter((l) => l.statut !== STATUTS_REGLEMENT.ANNULE && l.statut !== "ANNULE");
  const annulees = lignes.filter((l) => l.statut === "ANNULE");
  return { vivantes, annulees };
}

/** The `?panneau=tiers:` key of a fournisseur (its bridge when it exists, else `fournisseur:<id>` — TWP-34). */
export function cleTiersFournisseur({ tiers_id = null, fournisseur_id = null } = {}) {
  if (tiers_id) return tiers_id;
  return fournisseur_id ? `fournisseur:${fournisseur_id}` : null;
}

/** The fournisseur id a `?panneau=tiers:` key names when it is a `fournisseur:<id>` key. */
export function fournisseurDeCle(cle) {
  const m = /^fournisseur:([0-9a-f-]{36})$/i.exec(String(cle ?? ""));
  return m ? m[1] : null;
}

/** The T2 tabs (fournisseur) this card renders, in the server's order (`profil.onglets`). */
export const ONGLETS_TIERS_FOURNISSEUR = Object.freeze(["factures_ouvertes", "releve", "reglements"]);

/** The checked open factures of T2 (only réglable rows) and whether « Régler la sélection » applies. */
export function selectionFacturesOuvertes(factures, ids) {
  const set = new Set(ids ?? []);
  const cochees = (factures ?? []).filter((f) => set.has(f.id) && f.reglable);
  return { cochees, ids: cochees.map((f) => f.id) };
}

// ─── TWP-66 (TO-17) — the avoir rows of « Factures ouvertes » and of the settlement form ─────────────────

/** The page of a facture / of an avoir, opened by « Consulter » in a new window (XWP-74). */
export const cheminFacture = (id) => `/achats/${id}`;
export const cheminAvoir = (id) => `/avoirs/${id}`;

/** Is this row of « Factures ouvertes » an avoir row (a confirmed credit no facture carries)? */
export const estLigneAvoir = (ligne) => ligne?.type_ligne === "AVOIR";

/**
 * The rows of « Factures ouvertes »: the factures (server order, TD-02), then the avoir rows (oldest
 * first) — each avoir row marked, its signed `reste` (a credit) as the server served it.
 */
export function lignesFacturesOuvertes(ouvertes) {
  const factures = (ouvertes?.factures ?? []).map((f) => ({ ...f, type_ligne: "FACTURE" }));
  const avoirs = (ouvertes?.avoirs_non_lies ?? []).map((a) => ({ ...a, type_ligne: "AVOIR" }));
  return [...factures, ...avoirs];
}

/**
 * A grid selection split into its réglable factures and its avoir rows (ids of rows no longer listed
 * are dropped).
 */
export function separerSelectionOuverte(lignes, ids) {
  const set = new Set(ids ?? []);
  const factures = [];
  const avoirs = [];
  for (const l of lignes ?? []) {
    if (!set.has(l.id)) continue;
    if (estLigneAvoir(l)) avoirs.push(l.id);
    else if (l.reglable) factures.push(l.id);
  }
  return { factures, avoirs };
}

/**
 * « Montant maximum à payer » of a selection (TO-17): Σ reste of the checked factures − Σ credit of the
 * checked avoirs, exact decimal strings (never a float), never below 0 — the excess credit stays at the
 * fournisseur level (`credit`). The form's figure is the server's (`montant_maximum`, the caps applied).
 */
export function montantMaximumSelection(lignes, { factures = [], avoirs = [] } = {}) {
  const fs = new Set(factures);
  const as = new Set(avoirs);
  let sf = "0";
  let sa = "0";
  for (const l of lignes ?? []) {
    if (estLigneAvoir(l) && as.has(l.id)) sa = addDecimal(sa, l.credit_non_impute ?? "0");
    else if (!estLigneAvoir(l) && fs.has(l.id)) sf = addDecimal(sf, l.reste ?? "0");
  }
  const oppose = (v) => (v.startsWith("-") ? v.slice(1) : `-${v}`);
  const net = addDecimal(sf, oppose(sa));
  const positif = compareDecimal(net, "0") > 0;
  return {
    factures: toDecimalString(sf),
    avoirs: toDecimalString(sa),
    maximum: positif ? toDecimalString(net) : "0",
    credit: positif || compareDecimal(net, "0") === 0 ? "0" : toDecimalString(oppose(net)),
  };
}

/**
 * The avoir rows a settlement form sends: none on an existing settlement or in « Par facture »; the
 * ones chosen (still offered), or — never chosen — every one offered when the caller may confirm
 * (a manager who only prepares drafts starts without them: a draft never imputes avoirs).
 */
export function avoirsEffectifs(etat, ouvertes, { modification = false } = {}) {
  if (modification || etat?.mode === MODES_VENTILATION.MANUEL) return [];
  const offerts = (ouvertes?.avoirs_non_lies ?? []).map((a) => a.id);
  if (etat?.avoirs == null) return ouvertes?.droits?.confirmer?.autorise ? offerts : [];
  const choisis = new Set(etat.avoirs);
  return offerts.filter((id) => choisis.has(id));
}

/** Check / uncheck one avoir row of the form (from the effective list, so the default becomes explicit). */
export function basculerAvoir(etat, id, effectifs) {
  const courant = new Set(effectifs ?? []);
  if (courant.has(id)) courant.delete(id);
  else courant.add(id);
  return { ...etat, avoirs: [...courant] };
}
